/**
 * Palette Rail to surface and feature more than one promotional or product card at a time.
 *
 * @module views/components/Rail
 * @memberof -Common
 */
import './Rail.scss';

import React, { useRef } from 'react';

import PropTypes from 'prop-types';
import { Rail as PalRail, Slide as PalSlide } from 'web-palette/dist/components/Rail/Rail';
import { actionFormatter } from 'web-palette/dist/util/actionFormatter/actionFormatter';

import Button from '@ulta/core/components/Button/Button';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import { handleSwiperImpressionEvent } from '@ulta/core/utils/datacaptureAggregator/datacaptureAggregator';
import { getButtonHtml } from '@ulta/core/utils/getButtonHtml/getButtonHtml';
import { getLinkHtml } from '@ulta/core/utils/getLinkHtml/getLinkHtml';

import { handleSwiperSlideChange } from '@ulta/utils/carouselUtils/carouselUtils';

/**
 * Represents a Rail component
 *
 * @method
 * @param { RailProps} props - React properties passed from composition
 * @returns Rail
 */
export const Rail = function( props ){
  const carouselRef = useRef( null );

  const {
    /* Array of items going into Rail */
    children,
    items,

    /* Actions coming from web-core */
    viewAllAction, // linkAction for RailTitle
    previousClickAction, // Rail back button
    nextClickAction, // Rail forward button

    /* spotlight card */
    externalElementMobileSpotlightCard,

    /* TitlesDefault Props */
    headline,
    headlineTag,
    subheadline,

    /* Slider Props */
    centeredSlides = defaultProps.centeredSlides,
    externalSmallDevice,
    id, // amplience component ID for the previous/next buttons
    isHighlighter,
    mobilePeek = defaultProps.mobilePeek,
    railRef, // rail ref from each rail implementation
    showBullets = defaultProps.showBullets,
    showCountLabel = defaultProps.showCountLabel,
    slidesPerViewAuto = defaultProps.slidesPerViewAuto,
    sliderPerViewXL,
    sliderPerViewLG,
    sliderHighlightPerViewLG,
    sliderPerViewMD,
    sliderPerViewSM,
    slidesPerGroupXL,
    slidesPerGroupLG,
    slidesHighlightPerGroupLG,
    slidesPerGroupMD,
    slidesPerGroupSM,
    spaceBetween = defaultProps.spaceBetween,
    tabletPeek = defaultProps.tabletPeek,
    useFreeMode = defaultProps.useFreeMode,
    useObserver = defaultProps.useObserver,
    useObserveSlideChildren = defaultProps.observeSlideChildren,
    _railADAmeta, // contains carouselAccessibility, carouselDetailsAccessibility
    _railMeta // contains countLabel, nextAccessibility, previousAccessibility
  } = props;

  useIntersectionObserver(
    handleSwiperImpressionEvent( props, carouselRef, isHighlighter )
  );

  /**
   * ADA Accessibility
   */
  const { nextAccessibility, previousAccessibility } = _railMeta || {};

  /** Objects for external media and actions coming from Amplience */
  let formattedViewAllAction;

  /** Formatters for incoming media and actions */
  const formattedPreviousAction = getCarouselButton( {
    accessibility: previousAccessibility,
    carouselRef: carouselRef,
    className: 'pal-c-Rail__carousel--arrowBack',
    clickAction: previousClickAction,
    iconName: 'arrowBack',
    isHighlighter: isHighlighter,
    label: 'Arrow Left',
    props: props,
    railRef: railRef
  } );
  const formattedNextAction = getCarouselButton( {
    accessibility: nextAccessibility,
    carouselRef: carouselRef,
    className: 'pal-c-Rail__carousel--arrowForward',
    clickAction: nextClickAction,
    iconName: 'arrowForward',
    isHighlighter: isHighlighter,
    label: 'Arrow Right',
    props: props,
    railRef: railRef
  } );

  if( !!viewAllAction && viewAllAction.label ){
    formattedViewAllAction = actionFormatter(
      { action: viewAllAction },
      { getButtonMarkup: getButtonHtml,
        getLinkMarkup: getLinkHtml
      } );
  }

  return (
    <div className='Rail'>
      <PalRail
        /* Props for the Rail Title */
        externalActionElementLink={ formattedViewAllAction } /* Link for Rail title */
        externalActionElementBackButton={ formattedPreviousAction } /* The back button for the carousel */
        externalActionElementForwardButton={ formattedNextAction } /* The forward button for the carousel */
        headline={ headline } /* Rail headline */
        headlineTag={ headlineTag } /* Rail headline tag */
        showCountLabel={ showCountLabel } /* Boolean to show the count label */
        subheadline={ subheadline } /* Rail subheadline */
        /* Props for main carousel */
        carouselRef={ carouselRef } /* Carousel ref */
        centeredSlides={ centeredSlides } /* Default is false - Boolean to set the slides to be centered */
        externalSmallDevice={ externalSmallDevice }
        id={ id } /* Add this to each Rail implementation for the previous/next buttons */
        items={ items } /* Rail items */
        mobilePeek={ mobilePeek } /* Default is true - sets peek on mobile */
        showBullets={ showBullets } /* Default is false - Boolean to show bullets on MD/SM below image */
        spaceBetween={ spaceBetween } /* Default is 16 - Only change if needed (PillCarousel needs this) */
        tabletPeek={ tabletPeek } /* Default is false - sets peek on tablet */
        useFreeMode={ useFreeMode } /* Default is true - Free mode option for mobile */
        useObserver={ useObserver } /* Default is false - CarouselDesktop uses this */
        useObserveSlideChildren={ useObserveSlideChildren } /* Default is false - CarouselDesktop uses this */
        /* SliderPerView and SliderPerGroup settings */
        sliderPerViewLG={ sliderPerViewLG } /* Default is 3 */
        sliderPerViewMD={ sliderPerViewMD } /* Default is 3 */
        sliderPerViewSM={ sliderPerViewSM } /* Default is 1.6 for peek (1 for single card) */
        sliderPerViewXL={ sliderPerViewXL } /* Default is 4 */
        slidesPerGroupLG={ slidesPerGroupLG } /* Default is 3 */
        slidesPerGroupMD={ slidesPerGroupMD } /* Default is 3 */
        slidesPerGroupSM={ slidesPerGroupSM } /* Default is 1 */
        slidesPerGroupXL={ slidesPerGroupXL } /* Default is 4 */
        slidesPerViewAuto={ slidesPerViewAuto } /* Boolean to set sliders per view to be auto */
        /* Props for ProductCard Highlight */
        externalElementMobileSpotlightCard={ externalElementMobileSpotlightCard } /* The HighlighterCard */
        isHighlighter={ isHighlighter } /* Boolean to show the HighlighterCard */
        slidesHighlightPerGroupLG={ slidesHighlightPerGroupLG } /* Need to set if you have a highlighterCard */
        sliderHighlightPerViewLG={ sliderHighlightPerViewLG } /* Need to set if you have a highlighterCard */
        /* Props for ADA Meta */
        _railADAmeta={ _railADAmeta }
        _railMeta={ _railMeta }
      >
        { children }
      </PalRail>
    </div>
  );
};

/**
 * Function carousel buttons
 * @function getCarouselButton
 * @param { object }  - returns an object with the appropriate HTML markup
 */
export const getCarouselButton = ( data ) => {
  const {
    accessibility,
    carouselRef,
    clickAction,
    className,
    iconName,
    isHighlighter,
    label,
    props,
    railRef
  } = data;

  return (
    <Button
      ariaHiddenIcon={ true }
      className={ className }
      iconName={ iconName }
      iconOnly
      label={ label }
      onClick={ handleSwiperSlideChange(
        null,
        clickAction,
        carouselRef,
        handleSwiperImpressionEvent( props, carouselRef, isHighlighter ),
        railRef?.current
      ) }
      screenReaderText={ accessibility }
      size='tiny'
      variant='secondary'
    />
  );
};

// this is needed to fix this Swiper issue https://github.com/nolimits4web/swiper/issues/4413
export const Slide = PalSlide;
Slide.displayName = 'SwiperSlide';

/**
 * Property type definitions
 * @typedef RailProps
 * @type {object}
 * @property {any} carouselRef - Carousel reference from website
 * @property {boolean} centeredSlides - Sets the slides to be centered
 * @property {node} children - Children slot where components can be nested
 * @property {object} externalLinkAction - external action object
 * @property {object} externalGraphicImage - external graphic image object
 * @property {boolean} externalSmallDevice - Boolean for being a small device
 * @property {string} headline - Sets the headline title.
 * @property {string} headlineTag - Sets the headline title tag.
 * @property {string} id - Amplience component ID for the previous/next buttons
 * @property {boolean} isHighlighter - Boolean for the highlighter card
 * @property {array} items - Set the Item array.
 * @property {object} linkAction - Sets the url for action label.
 * @property {boolean} mobilePeek - Boolean for the right hand peek on mobile
 * @property {boolean} showBullets - Boolean for the pagination bullets
 * @property {boolean} showCountLabel - Boolean for the count label in the header
 * @property {number} sliderHighlightPerViewLG - Sets the sliders per view with highlight card on LG.
 * @property {number} sliderPerViewXL - Sets the sliders per view on XL.
 * @property {number} sliderPerViewLG - Sets the sliders per view on LG.
 * @property {number} sliderPerViewMD - Sets the sliders per view on MD.
 * @property {number} sliderPerViewSM - Sets the sliders per view on SM.
 * @property {boolean} slidesPerViewAuto - Sets the sliders per view to be auto
 * @property {number} slidesHighlightPerGroupLG - Sets the sliders per group with highlight card on LG.
 * @property {number} slidesPerGroupXL - Sets the sliders per group on XL.
 * @property {number} slidesPerGroupLG - Sets the sliders per group on LG.
 * @property {number} slidesPerGroupMD - Sets the sliders per group on MD.
 * @property {number} slidesPerGroupSM - Sets the sliders per group on SM.
 * @property {number} spaceBetween - Sets spacebetween number on the slides
 * @property {object} spotlight - Object of the details for the spotlight
 * @property {string} subheadline - Sets the subtitle.
 * @property {boolean} tabletPeek - Boolean for the right hand peek on tablet
 * @property {boolean} useFreeMode - Boolean whether the FreeMode is enabled
 * @property {boolean} useObserver - Boolean whether the useObserver is enabled
 * @property {boolean} useObserveSlideChildren - Boolean whether the useObserveSlideChildren is enabled
 * @property {object} _railADAmeta - Sets carouselAccessibility
 * @property {object} _railMeta - Sets nextAccessibility, previousAccessibility
 */
export const propTypes =  {
  /**
   * Carousel reference from website
   */
  carouselRef: PropTypes.any,
  /**
   * Sets the slides to be centered
   */
  centeredSlides: PropTypes.bool,
  /**
   * Children slot where components can be nested
   */
  children: PropTypes.node,
  /**
   * Sets the primary action from an external source that contains business logic
   */
  externalLinkAction: PropTypes.object,
  /**
   * Sets the graphic image from an external source that contains business logic
   */
  externalGraphicImage: PropTypes.object,
  /**
   * Boolean for being a small device
   */
  externalSmallDevice: PropTypes.bool,
  /**
   * Sets the headline title.
   */
  headline: PropTypes.string,
  /**
   * Sets the headline title tag.
   */
  headlineTag: PropTypes.string,
  /**
   * Amplience component ID for the previous/next buttons
   */
  id: PropTypes.string,
  /**
   * Boolean for the highlighter card
   */
  isHighlighter: PropTypes.bool,
  /**
   * Set the Item array.
   */
  items: PropTypes.array,
  /**
   * Sets the url for action label.
   */
  linkAction: PropTypes.object,
  /**
   * Boolean for the right hand peek on mobile
   */
  mobilePeek: PropTypes.bool,
  /**
   * Boolean for the pagination bullets
   */
  showBullets: PropTypes.bool,
  /**
   * Boolean for the count label in the header
   */
  showCountLabel: PropTypes.bool,
  /**
   * Sets the sliders per view with highlight card on LG.
   */
  sliderHighlightPerViewLG: PropTypes.number,
  /**
   * Sets the sliders per view on XL.
   */
  sliderPerViewXL: PropTypes.number,
  /**
   * Sets the sliders per view on LG.
   */
  sliderPerViewLG: PropTypes.number,
  /**
   * Sets the sliders per view on MD.
   */
  sliderPerViewMD: PropTypes.number,
  /**
   * Sets the sliders per view on SM.
   */
  sliderPerViewSM: PropTypes.number,
  /**
   * Sets the sliders per group with highlight card on LG.
   */
  slidesHighlightPerGroupLG: PropTypes.number,
  /**
   * Sets the sliders per group on XL.
   */
  slidesPerGroupXL: PropTypes.number,
  /**
   * Sets the sliders per group on LG.
   */
  slidesPerGroupLG: PropTypes.number,
  /**
   * Sets the sliders per group on MD.
   */
  slidesPerGroupMD: PropTypes.number,
  /**
   * Sets the sliders per group on SM.
   */
  slidesPerGroupSM: PropTypes.number,
  /**
   * Sets the sliders per view to be auto
   */
  slidesPerViewAuto: PropTypes.bool,
  /**
   * Sets spacebetween number on the slides
   */
  spaceBetween: PropTypes.number,
  /**
   * Object of the details for the spotlight
   */
  spotlight: PropTypes.object,
  /**
   * Sets the subtitle.
   */
  subheadline: PropTypes.string,
  /**
   * Boolean for the right hand peek on tablet
   */
  tabletPeek: PropTypes.bool,
  /**
   * Boolean whether the FreeMode is enabled
   */
  useFreeMode: PropTypes.bool,
  /**
   * Boolean whether the useObserver is enabled
   */
  useObserver: PropTypes.bool,
  /**
   * Boolean whether the useObserveSlideChildren is enabled
   */
  useObserveSlideChildren: PropTypes.bool,
  /**
   * Sets carouselAccessibility
   */
  _railADAmeta: PropTypes.object,
  /**
   * Sets nextAccessibility, previousAccessibility
   */
  _railMeta: PropTypes.object
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {boolean} centeredSlides = 'false' - Sets the slides to be centered
 * @property {boolean} mobilePeek='true' - Boolean for the right hand peek on mobile
 * @property {boolean} showCountLabel='false' - Boolean for the count label in the header
 * @property {boolean} showBullets='false' - Boolean for the pagination bullets
 * @property {boolean} slidesPerViewAuto='false' - Sets the sliders per view to be auto
 * @property {number} spaceBetween='16' - Sets spacebetween number on the slides
 * @property {boolean} tabletPeek='false' - Boolean for the right hand peek on tablet
 * @property {boolean} useFreeMode='true' - Boolean whether the FreeMode is enabled
 * @property {boolean} useObserver='false' - Boolean whether the useObserver is enabled
 * @property {boolean} useObserveSlideChildren='false' - Boolean whether the useObserveSlideChildren is enabled
 */
export const defaultProps =  {
  centeredSlides: false,
  mobilePeek: true,
  showBullets: false,
  showCountLabel: false,
  slidesPerViewAuto: false,
  spaceBetween: 16,
  tabletPeek: false,
  useFreeMode: true,
  useObserver: false,
  useObserveSlideChildren: false
};

Rail.propTypes = propTypes;

export default Rail;